import type { AvatarHostLiveConfig } from '../host/adapter';

/**
 * Result of resolving the host-facing `live.transport` selector into the
 * concrete live-config the rest of `AvatarEmbed` consumes.
 */
export interface ResolvedLiveTransport {
  /**
   * True when the host selected `transport: 'voice-call'` — the visual-only
   * sync-to-an-external-SIP-call transport. The embed forces visual-only mode
   * (no WebRTC INVITE, no microphone, no local TTS) when this is true.
   */
  isVoiceCallTransport: boolean;
  /**
   * The live config the rest of `AvatarEmbed` should consume.
   *
   * For `transport: 'voice-call'` this is normalized to a NON-contextual-chat,
   * NON-WebRTC visual session: the `liveSessionKind` is forced to
   * `'webrtc-call'` (any non-`contextual-chat` value works — it just has to
   * NOT be `contextual-chat`), and `liveDestination` / `sipWebSocketUrl` /
   * `contextualChat` are dropped. This is what makes the SDK `visual.link`-
   * subscribe the phone's session (with the existing `session-not-found`
   * retry in `LiveModeClient`) instead of issuing a competing contextual-chat
   * `session.start` on the same `handoffToken` — the duplicate-session crux
   * the Jun-21 2026 transport plan calls out. The phone owns BOTH the audio
   * and the conversation brain, so the browser-LLM contextual-chat seed is
   * deliberately discarded.
   *
   * For every other transport (including the default `'webrtc'` and the
   * undefined-config case) this is the input returned unchanged, so existing
   * consumers are byte-for-byte unaffected.
   */
  effectiveLiveConfig: AvatarHostLiveConfig | undefined;
}

/**
 * Map the host-facing `live.transport` selector onto the concrete live-config
 * + visual-only decision. Pure so the contract is unit-testable without React.
 *
 * Phase 1 of the avatar Live + Visual IVR transport convergence
 * (`camlin-workspace/docs/AVATAR_LIVE_VISUAL_IVR_TRANSPORT_PLAN_JUN_21_2026.md`).
 */
export function resolveLiveTransportConfig(
  rawLiveConfig: AvatarHostLiveConfig | undefined
): ResolvedLiveTransport {
  const isVoiceCallTransport = rawLiveConfig?.transport === 'voice-call';

  if (!rawLiveConfig || !isVoiceCallTransport) {
    return { isVoiceCallTransport: false, effectiveLiveConfig: rawLiveConfig };
  }

  return {
    isVoiceCallTransport: true,
    effectiveLiveConfig: {
      ...rawLiveConfig,
      liveSessionKind: 'webrtc-call',
      liveDestination: undefined,
      sipWebSocketUrl: undefined,
      contextualChat: undefined,
    },
  };
}
